import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Target } from "lucide-react";
import MerchantWaitlist from "./MerchantWaitlist";

/**
 * ComingSoonModal — surfaces the merchant email capture form.
 *
 * Two copy modes:
 *   • mode="waitlist" (default) — original "launching soon" framing. Used on
 *     surfaces that still communicate a pre-launch waitlist.
 *   • mode="qualify" — "see what your business qualifies for" framing. Used
 *     by the homepage merchant CTAs now that ZenMe is accepting applications.
 *
 * Both modes POST to the same /api/waitlist/merchant endpoint using the
 * `source` value passed in from the caller. Attribution is aggregated by
 * `source` in the admin panel.
 */
const COPY = {
  waitlist: {
    eyebrow: "Launching Soon",
    eyebrowIcon: Sparkles,
    title: "ZenMe is launching soon.",
    description: "Join the waitlist for early access.",
    ctaLabel: "Join Waitlist",
    successMessage: "Thanks — we'll be in touch.",
    caption: null,
  },
  qualify: {
    eyebrow: "Business Funding",
    eyebrowIcon: Target,
    title: "See what your business qualifies for.",
    description:
      "Enter your business email — we'll follow up with next steps for your funding application.",
    ctaLabel: "Continue",
    successMessage: "Thanks — we'll be in touch shortly with next steps.",
    caption:
      "Applications are reviewed within 1–2 business days. Funding decisions are subject to underwriting and funding partner approval.",
  },
};

export const ComingSoonModal = ({
  open,
  onOpenChange,
  source = "waitlist_modal",
  mode = "waitlist",
}) => {
  const copy = COPY[mode] ?? COPY.waitlist;
  const EyebrowIcon = copy.eyebrowIcon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg overflow-hidden rounded-2xl border-white/[0.08] bg-bg-raised p-0 text-white"
        data-testid="coming-soon-modal"
        data-mode={mode}
      >
        <div className="relative overflow-hidden px-8 pt-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -top-40 h-72 opacity-70 blur-3xl"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(0,230,118,0.35) 0%, rgba(0,0,0,0) 70%)",
            }}
          />

          <div className="relative flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-emerald">
              <EyebrowIcon className="h-3 w-3" />
              {copy.eyebrow}
            </span>
          </div>

          <DialogTitle className="relative mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            {copy.title}
          </DialogTitle>

          <DialogDescription className="relative mt-3 text-sm text-text-secondary sm:text-base">
            {copy.description}
          </DialogDescription>
        </div>

        <div className="px-8 pb-8 pt-6">
          <MerchantWaitlist
            source={source}
            ctaLabel={copy.ctaLabel}
            successMessage={copy.successMessage}
            data-testid="modal-waitlist"
          />
          {copy.caption && (
            <p
              className="mt-4 text-xs leading-relaxed text-text-muted"
              data-testid="modal-caption"
            >
              {copy.caption}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComingSoonModal;
